import type { OptimisticUpdate } from "convex/browser";
import { useAction, useMutation } from "convex/react";
import type { FunctionArgs, FunctionReference, FunctionReturnType } from "convex/server";
import { useCallback, useMemo } from "react";
import { retryActionOnConnectionLoss } from "@/lib/convex-action-retry";
import {
  breadcrumb,
  type ObservedFailureClassifier,
  type ObservedOperationKind,
  trackObservedOperation,
} from "@/lib/observability";

interface ObservedConvexOptions<TArgs, TResult> {
  feature: string;
  operation: string;
  /**
   * Adds the frontend requestId to Convex args for end-to-end correlation.
   * Only enable this for Convex functions whose validators accept requestId
   * directly, normally via trackedQuery/trackedMutation/trackedAction. If a
   * handler forwards args to internal functions, build explicit payloads and
   * include requestId only where the internal validator accepts it.
   */
  includeRequestId?: boolean;
  context?: Record<string, unknown> | ((args: TArgs) => Record<string, unknown>);
  failureOutcome?: string;
  classifyFailure?: ObservedFailureClassifier<TResult>;
}

function resolveContext<TArgs>(
  context: ObservedConvexOptions<TArgs, unknown>["context"],
  args: TArgs
): Record<string, unknown> | undefined {
  return typeof context === "function" ? context(args) : context;
}

export function addRequestIdToConvexArgs<TArgs>(
  args: TArgs,
  requestId: string,
  includeRequestId: boolean | undefined
): TArgs {
  if (!includeRequestId || !args || typeof args !== "object" || Array.isArray(args)) {
    return args;
  }

  return {
    ...args,
    requestId,
  };
}

function trackConvexFunctionCall<TArgs, TResult>(options: {
  kind: ObservedOperationKind;
  args: TArgs;
  includeRequestId?: boolean;
  feature: string;
  operation: string;
  context?: ObservedConvexOptions<TArgs, TResult>["context"];
  failureOutcome?: string;
  classifyFailure?: ObservedFailureClassifier<TResult>;
  execute: (args: TArgs) => Promise<TResult>;
}) {
  return trackObservedOperation({
    kind: options.kind,
    feature: options.feature,
    operation: options.operation,
    context: resolveContext(options.context, options.args),
    failureOutcome: options.failureOutcome,
    classifyFailure: options.classifyFailure,
    execute: (requestId) =>
      options.execute(addRequestIdToConvexArgs(options.args, requestId, options.includeRequestId)),
  });
}

export function useObservedMutation<Mutation extends FunctionReference<"mutation">>(
  mutation: Mutation,
  options: ObservedConvexOptions<FunctionArgs<Mutation>, FunctionReturnType<Mutation>> & {
    optimisticUpdate?: OptimisticUpdate<FunctionArgs<Mutation>>;
  }
) {
  const rawMutation = useMutation(mutation);
  const {
    classifyFailure,
    context,
    failureOutcome,
    feature,
    includeRequestId,
    operation,
    optimisticUpdate,
  } = options;
  const runMutation = useMemo(
    () => (optimisticUpdate ? rawMutation.withOptimisticUpdate(optimisticUpdate) : rawMutation),
    [optimisticUpdate, rawMutation]
  );

  return useCallback(
    (args: FunctionArgs<Mutation>) =>
      trackConvexFunctionCall({
        kind: "mutation",
        args,
        includeRequestId,
        feature,
        operation,
        context,
        failureOutcome,
        classifyFailure,
        execute: (nextArgs) => runMutation(nextArgs),
      }),
    [classifyFailure, context, failureOutcome, feature, includeRequestId, operation, runMutation]
  );
}

type ObservedActionOptions<TArgs, TResult> = ObservedConvexOptions<TArgs, TResult> & {
  /**
   * Re-runs the action with identical args (the same `requestId` included) when the Convex
   * client drops it on a WebSocket reconnect. That rejection means the outcome is unknown —
   * the action may have completed — so this is only for actions whose server side is
   * idempotent on their args: `artifactMedia.createMediaFile` (through
   * `createOrReuseMediaArtifact`, which returns the artifact already registered for an
   * `r2Key`) and `artifactMedia.createMediaAttachment` (through `registerMediaAttachment`,
   * idempotent on thread and `r2Key`). Mutations need no such option: the client re-sends
   * them on reconnect and the server dedupes them.
   *
   * The retries run inside one observed operation, so a recovered call is never captured and
   * an exhausted one is captured once; each retry leaves a warning breadcrumb.
   */
  retryOnConnectionLoss?: boolean;
};

/** Per-call options for an observed action. */
export type ObservedActionCallOptions = {
  /**
   * Stops connection-loss retries: once it aborts, no further attempt starts, a wait between
   * attempts ends early, and at either point the call rejects with an `AbortError`
   * `DOMException`. An attempt already running is not interrupted — if it resolves, the call
   * resolves with its result. Has no effect without `retryOnConnectionLoss`.
   */
  signal?: AbortSignal;
};

/**
 * The action call `createObservedActionCaller` hands to `trackObservedOperation`, wrapped in
 * the connection-loss retry when the caller opted in. `delaysMs` exists for tests.
 */
export function buildObservedActionExecute<TArgs, TResult>(
  runAction: (args: TArgs) => Promise<TResult>,
  options: {
    feature: string;
    operation: string;
    retryOnConnectionLoss?: boolean;
    delaysMs?: readonly number[];
  }
): (args: TArgs, callOptions?: ObservedActionCallOptions) => Promise<TResult> {
  const { delaysMs, feature, operation, retryOnConnectionLoss } = options;
  if (!retryOnConnectionLoss) return (args) => runAction(args);

  return (args, callOptions) =>
    retryActionOnConnectionLoss(() => runAction(args), {
      delaysMs,
      signal: callOptions?.signal,
      onRetry: (attempt, error) => {
        const requestId =
          args && typeof args === "object" && "requestId" in args ? args.requestId : undefined;
        breadcrumb(
          `${feature}.${operation}.retry_after_connection_loss`,
          { requestId, attempt, error: error.message },
          { category: "action", level: "warning" }
        );
      },
    });
}

/**
 * The observed action call without React: one `trackObservedOperation` per call (one
 * `requestId`, one `.start` breadcrumb, at most one capture) around the raw action, with any
 * connection-loss retries inside it. `useObservedAction` is this memoised over `useAction`.
 * `delaysMs` exists for tests and is not a hook option.
 */
export function createObservedActionCaller<TArgs, TResult>(
  rawAction: (args: TArgs) => Promise<TResult>,
  options: ObservedActionOptions<TArgs, TResult> & { delaysMs?: readonly number[] }
): (args: TArgs, callOptions?: ObservedActionCallOptions) => Promise<TResult> {
  const {
    classifyFailure,
    context,
    delaysMs,
    failureOutcome,
    feature,
    includeRequestId,
    operation,
    retryOnConnectionLoss,
  } = options;
  const runAction = buildObservedActionExecute(rawAction, {
    feature,
    operation,
    retryOnConnectionLoss,
    delaysMs,
  });

  return (args, callOptions) =>
    trackConvexFunctionCall({
      kind: "action",
      args,
      includeRequestId,
      feature,
      operation,
      context,
      failureOutcome,
      classifyFailure,
      execute: (nextArgs) => runAction(nextArgs, callOptions),
    });
}

export function useObservedAction<Action extends FunctionReference<"action">>(
  action: Action,
  options: ObservedActionOptions<FunctionArgs<Action>, FunctionReturnType<Action>>
) {
  const rawAction = useAction(action);
  const {
    classifyFailure,
    context,
    failureOutcome,
    feature,
    includeRequestId,
    operation,
    retryOnConnectionLoss,
  } = options;

  return useMemo(
    () =>
      createObservedActionCaller((nextArgs: FunctionArgs<Action>) => rawAction(nextArgs), {
        classifyFailure,
        context,
        failureOutcome,
        feature,
        includeRequestId,
        operation,
        retryOnConnectionLoss,
      }),
    [
      classifyFailure,
      context,
      failureOutcome,
      feature,
      includeRequestId,
      operation,
      rawAction,
      retryOnConnectionLoss,
    ]
  );
}
